import {
  Component,
  EventEmitter,
  AfterViewInit,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  OnDestroy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ConfigService } from "src/app/provider/config.service";
import { Subscription, Subject } from "rxjs";

@Component({
  selector: "menu-item",
  templateUrl: "./menu-item.component.html",
  styleUrls: ["./menu-item.component.scss"],
  standalone: false,
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() item?: any;
  @Input() isForceFixedMenu: boolean = false;
  @Input() mealsCount: number = 0;
  @Input() snacksCount: number = 0;
  @Input() is_variable_carbs?: boolean;
  @Input() max_scaled_carbs?: number;
  @Input() dayConfigEntry?: any;
  @Input() defaultCarbs?: number;

  @Output() quantityChangeRequest = new EventEmitter<{
    itemId: number;
    isPlusClick: boolean;
  }>();
  @Output() itemSelectionChanged = new EventEmitter<any>();
  @Output() menuDetailRequested = new EventEmitter<any>();
  @Output() carbSelectionChanged = new EventEmitter<{
    itemId: number;
    scaledCarbs: number;
  }>();

  appL: string = "en";
  public settings?: any;
  private appSettingsSubscription?: Subscription;

  selectionLimit: { [key: string]: number } = {};
  private destroy$ = new Subject<void>();
  isMeal?: boolean;
  limit?: any;

  // Scaled carbs properties
  scaledCarbOptions: number[] = [];
  selectedScaledCarb: number = 0;
  showScaledCarbSelector: boolean = false;

  constructor(
    private translate: TranslateService,
    private configService: ConfigService,
    private cdRef: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.appSettingsSubscription = this.configService.appSettings$.subscribe(
      (currentSettings: any) => {
        this.settings = currentSettings;
        this.appL = this.settings.appLanguage;
        this.cdRef.markForCheck();
      }
    );
    this.isMeal = this.item?.food_type_category == "meal" ? true : false;
    this.limit = this.isMeal ? this.mealsCount : this.snacksCount;

    // Initialize scaled carb options
    this.initializeScaledCarbOptions();

    // Set initial selected carb value
    this.selectedScaledCarb =
      this.item?.scaled_carbs || this.defaultCarbs || 50;

    // Show selector if item is already selected with quantity > 0
    this.updateScaledCarbVisibility();
  }

  ngAfterViewInit() {
    // Nothing extra needed for OnPush
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.appSettingsSubscription)
      this.appSettingsSubscription?.unsubscribe();
  }

  // ✅ Toggle selection
  // onSelectFoodMenu() {
  //   if (!this.item) return;

  //   if (this.item.quantity === 0) {
  //     this.onPlusClick();
  //   } else if (this.item.quantity == this.limit) {
  //     return;
  //   } else {
  //     this.onMinusClick();
  //   }
  // }

  onSelectFoodMenu() {
    if (!this.item) return;

    // TOGGLE LOGIC
    if (this.item.quantity > 0) {
      // UNSELECT
      this.quantityChangeRequest.emit({
        itemId: this.item.id,
        isPlusClick: false,
      });
    } else {
      // SELECT
      this.quantityChangeRequest.emit({
        itemId: this.item.id,
        isPlusClick: true,
      });
    }

    setTimeout(() => this.updateScaledCarbVisibility(), 100);
  }

  // ✅ Plus / Minus clicks emit event to parent
  onMinusClick() {
    if (!this.item || this.item.quantity === 0) {
      return;
    }
    this.quantityChangeRequest.emit({
      itemId: this.item.id,
      isPlusClick: false,
    });

    // Update scaled carb visibility after quantity change
    setTimeout(() => this.updateScaledCarbVisibility(), 100);
  }

  onPlusClick() {
    if (!this.item || this.item.quantity == this.limit) {
      return;
    }
    this.quantityChangeRequest.emit({
      itemId: this.item.id,
      isPlusClick: true,
    });

    // Update scaled carb visibility after quantity change
    setTimeout(() => this.updateScaledCarbVisibility(), 100);
  }

  onMenuDetailPage() {
    this.menuDetailRequested.emit({ item: this.item });
  }

  // Shorten title/description for display
  getShortTitle = (data: string): string => {
    if (!data) return "";
    return data.length > 39 ? data.substring(0, 39) + "..." : data;
  };

  getShortDesc = (data: string): string => {
    if (!data) return "";
    return data.length > 49 ? data.substring(0, 49) + "..." : data;
  };

  getParseFloat = (num: any): number => Number(parseFloat(num).toFixed(0));

  /**
   * Initialize scaled carb options based on max_scaled_carbs
   * Creates array of values in increments of 25 from 0 to max
   */
  private initializeScaledCarbOptions(): void {
    if (!this.is_variable_carbs) {
      this.scaledCarbOptions = [];
      return;
    }

    const maxCarbs = this.max_scaled_carbs || 100;
    this.scaledCarbOptions = [];

    // Generate options: 0, 25, 50, 75, 100, etc.
    for (let i = 0; i <= maxCarbs; i += 25) {
      this.scaledCarbOptions.push(i);
    }

    // Ensure max value is included if not divisible by 25
    if (maxCarbs % 25 !== 0 && !this.scaledCarbOptions.includes(maxCarbs)) {
      this.scaledCarbOptions.push(maxCarbs);
    }
  }

  /**
   * Update visibility of scaled carb selector
   * Only show if item is selected (quantity > 0) and is_variable_carbs is true
   */
  private updateScaledCarbVisibility(): void {
    this.showScaledCarbSelector =
      this.is_variable_carbs === true &&
      this.item?.quantity > 0 &&
      this.scaledCarbOptions.length > 0;
  }

  /**
   * Handle scaled carb selection
   */
  onScaledCarbSelect(carbValue: number): void {
    if (!this.item || !this.is_variable_carbs) return;

    this.selectedScaledCarb = carbValue;
    this.item.scaled_carbs = carbValue;

    // Emit event to parent
    this.carbSelectionChanged.emit({
      itemId: this.item.id,
      scaledCarbs: carbValue,
    });

    this.cdRef.markForCheck();
  }

  /**
   * Check if a carb value is currently selected
   */
  isScaledCarbSelected(carbValue: number): boolean {
    return this.selectedScaledCarb === carbValue;
  }
}
